import React, { useState, useRef } from 'react';
import Record from './Record';
import PreviewVideo from './PreviewVideo';
import CameraVideo from './CameraVideo';

function Session() {
    const [sessionName, setSessionName] = useState<string>('');

    const [userMediaStream, setUserMediaStream] = useState<MediaStream | null>(
        null
    );

    const [
        displayMediaStream,
        setDisplayMediaStream
    ] = useState<MediaStream | null>(null);

    const [isRecording, setIsRecording] = useState<boolean>(false);

    const cameraVideoRef = useRef<HTMLVideoElement>(null);

    function isMicrophoneEnabled(): boolean {
        // Is this a reliable checK?
        return !!(
            userMediaStream &&
            userMediaStream.getAudioTracks &&
            userMediaStream.getAudioTracks().length > 0
        );
    }

    function isVideoEnabled(): boolean {
        // Is this a reliable checK?
        return !!(
            userMediaStream &&
            userMediaStream.getVideoTracks &&
            userMediaStream.getVideoTracks().length > 0
        );
    }

    function isScreenRecordingEnabled(): boolean {
        return !!displayMediaStream;
    }

    async function startDisplayMediaStream(): Promise<void> {
        const displayMediaStream = await (navigator as any).mediaDevices.getDisplayMedia(
            {
                video: true
            }
        );

        // Doesn't look like you can get audio from the DisplayMedia just yet so need to pluck it
        // out of the userMediaStream.
        if (userMediaStream && userMediaStream.getAudioTracks().length) {
            const userMediaStreamAudioTrack = userMediaStream.getAudioTracks()[0];
            displayMediaStream.addTrack(userMediaStreamAudioTrack);
        }

        setDisplayMediaStream(displayMediaStream);
    }

    function stopDisplayMediaStream(): void {
        if (!displayMediaStream) {
            return;
        }

        const tracks = [
            ...displayMediaStream.getAudioTracks(),
            ...displayMediaStream.getVideoTracks()
        ];

        tracks.forEach(track => track.stop());
        setDisplayMediaStream(null);
    }

    function toggleIsRecording(): void {
        setIsRecording(!isRecording);
    }

    async function toggleVideo() {
        let newUserMediaStream: null | MediaStream = null;
        debugger;
        const newConfig = {
            video: !isVideoEnabled(),
            audio: isMicrophoneEnabled()
        };

        if (isVideoEnabled()) {
            userMediaStream!.getVideoTracks().forEach(track => track.stop());
        } else {
            try {
                //@ts-ignore
                await cameraVideoRef.current!.requestPictureInPicture();
            } catch (e) {
                console.error(e.message);
            }
        }

        if (newConfig.video || newConfig.audio) {
            newUserMediaStream = await navigator.mediaDevices.getUserMedia(
                newConfig
            );
        }

        setUserMediaStream(newUserMediaStream);
    }

    async function toggleMicrophone() {
        let newUserMediaStream: null | MediaStream = null;
        const newConfig = {
            video: isVideoEnabled(),
            audio: !isMicrophoneEnabled()
        };

        if (isMicrophoneEnabled()) {
            userMediaStream!.getAudioTracks().forEach(track => track.stop());
        }

        if (newConfig.video || newConfig.audio) {
            newUserMediaStream = await navigator.mediaDevices.getUserMedia(
                newConfig
            );
        }

        setUserMediaStream(newUserMediaStream);
    }

    async function toggleScreenRecording(): Promise<void> {
        if (isScreenRecordingEnabled()) {
            await stopDisplayMediaStream();
        } else {
            await startDisplayMediaStream();
        }
    }

    return (
        <div className="sessionName">
            <div className="sessionHeader">
                <div className="">
                    <label htmlFor="sessionName">Name your recording</label>
                    <input
                        type="text"
                        data-testid="sessionNameInput"
                        value={sessionName}
                        placeholder="My first SeaState recording&hellip;"
                        disabled={isRecording ? true : false}
                        onChange={e => setSessionName(e.currentTarget.value)}
                    />
                </div>
                <div className="sessionConfig">
                    <button
                        className={
                            isScreenRecordingEnabled()
                                ? 'button is-on'
                                : 'button'
                        }
                        data-control="Screen"
                        data-testid="screenRecordingToggle"
                        data-control-action={
                            isScreenRecordingEnabled() ? 'Disable' : 'Record'
                        }
                        onClick={toggleScreenRecording}
                        disabled={isRecording ? true : false}
                    >
                        <svg
                            width="28"
                            height="28"
                            viewBox="0 0 31 31"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <g fillRule="nonzero" fill="none">
                                <g fill="#000">
                                    <path d="M15.1 23.4h2v3h-2z" />
                                    <path d="M12.1 26h8v2h-8zM29.1 7.4h-26a1 1 0 0 0-1 1.2v13.7a1 1 0 0 0 1 1.1h26c.6 0 1-.5 1-1.1V8.6a1 1 0 0 0-1-1.2zm-15 8v.3l4.4 1.7a2 2 0 1 1-.4 1v-.3l-4.3-1.6a2 2 0 1 1 0-2.1l4.3-1.6v-.4a2 2 0 1 1 .3 1.1l-4.3 1.6v.3z" />
                                </g>
                                <path
                                    className="line line-bottom"
                                    d="M0 2.6l1.7-.8 27 26.9-1.9 1.7z"
                                />
                                <path
                                    className="line line-top"
                                    d="M1.8 1.7L3.6 0l26.8 26.8-1.7 1.8z"
                                />
                            </g>
                        </svg>
                    </button>

                    <button
                        className={
                            isMicrophoneEnabled() ? 'button is-on' : 'button'
                        }
                        data-control="Microphone"
                        data-control-action={
                            isMicrophoneEnabled() ? 'Disable' : 'Record'
                        }
                        data-testid="microphoneToggle"
                        onClick={toggleMicrophone}
                        disabled={isRecording ? true : false}
                    >
                        <svg
                            width="28"
                            height="27"
                            viewBox="0 0 22 21"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <g fill="none" fillRule="evenodd">
                                <path
                                    d="M11 13a3 3 0 0 0 3-3V4a3 3 0 0 0-6 0v6a3 3 0 0 0 3 3zm5.3-3a5.2 5.2 0 0 1-5.3 5.1A5.2 5.2 0 0 1 5.7 10H4a7 7 0 0 0 6 6.7V20h2v-3.3a7 7 0 0 0 6-6.7h-1.7z"
                                    fillRule="nonzero"
                                />

                                <path
                                    fillRule="nonzero"
                                    className="line line-bottom"
                                    d="M.7 2.5l1.2-1.2 18.5 18.5-1.2 1.2z"
                                />
                                <path
                                    fill="#FFF"
                                    fillRule="nonzero"
                                    className="line line-top"
                                    d="M2 1.2L3.2 0l18.5 18.5-1.2 1.2z"
                                />
                            </g>
                        </svg>
                    </button>

                    <button
                        className={isVideoEnabled() ? 'button is-on' : 'button'}
                        data-control="Camera"
                        data-testid="videoToggle"
                        data-control-action={
                            isVideoEnabled() ? 'Disable' : 'Record'
                        }
                        disabled={isRecording ? true : false}
                        onClick={toggleVideo}
                    >
                        <svg
                            width="28"
                            height="27"
                            viewBox="0 0 21 20"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <g fill="none" fillRule="evenodd">
                                <path
                                    d="M15 9.5V6c0-.6-.4-1-1-1H2a1 1 0 0 0-1 1v10c0 .6.4 1 1 1h12c.6 0 1-.4 1-1v-3.5l4 4v-11l-4 4z"
                                    fillRule="nonzero"
                                />
                                <path
                                    fillRule="nonzero"
                                    className="line line-bottom"
                                    d="M.2 2.5l1.2-1.2L19 19l-1.2 1.2z"
                                />
                                <path
                                    fillRule="nonzero"
                                    className="line line-top"
                                    d="M1.5 1.3L2.6.2l17.6 17.6-1.1 1.1z"
                                />
                            </g>
                        </svg>
                    </button>
                </div>
            </div>
            <div className="screenPreview">
                {displayMediaStream && (
                    <PreviewVideo stream={displayMediaStream} />
                )}
            </div>
            <div className="record">
                <button
                    className="recording-button"
                    data-testid="recordSessionButton"
                    onClick={toggleIsRecording}
                    disabled={displayMediaStream ? false : true}
                >
                    {isRecording ? 'STOP' : 'REC'}
                </button>
            </div>

            <CameraVideo
                videoRef={cameraVideoRef}
                stream={
                    userMediaStream &&
                    userMediaStream.getVideoTracks().length &&
                    new MediaStream([userMediaStream.getVideoTracks()[0]])
                }
            />

            {isRecording && (
                <Record
                    screenRecordingStream={displayMediaStream}
                    userMediaStream={userMediaStream}
                    sessionName={sessionName}
                />
            )}
        </div>
    );
}

export default Session;
